import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './BlogList.css';

// Simple frontmatter parser for browser (no Buffer needed)
function parseFrontmatter(text) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = text.match(frontmatterRegex);
  
  if (!match) {
    return { data: {}, content: text };
  }
  
  const frontmatter = match[1];
  const content = match[2];
  const data = {};
  
  // Parse YAML-like frontmatter (simple key: value pairs)
  // Handle values that may contain colons by splitting on first colon only
  frontmatter.split('\n').forEach(line => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return; // Skip empty lines
    
    const colonIndex = trimmedLine.indexOf(':');
    if (colonIndex > 0) {
      const key = trimmedLine.substring(0, colonIndex).trim();
      let value = trimmedLine.substring(colonIndex + 1).trim();
      // Remove quotes if present
      value = value.replace(/^["']|["']$/g, '');
      data[key] = value;
    }
  });
  
  return { data, content };
}

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load all markdown files from public/posts
    const loadPosts = async () => {
      try {
        // List of all post files
        const postFiles = [
          'welcome-to-my-blog.md',
          'building-a-career-map.md',
          'nyc-ferry-observations.md'
        ];

        const postPromises = postFiles.map(async (filename) => {
          try {
            // Use process.env.PUBLIC_URL for proper path resolution
            const baseUrl = process.env.PUBLIC_URL || '';
            const response = await fetch(`${baseUrl}/posts/${filename}`);
            if (!response.ok) {
              console.error(`Failed to load ${filename}: ${response.status} ${response.statusText}`);
              return null;
            }
            const text = await response.text();
            const { data, content } = parseFrontmatter(text);
            return {
              ...data,
              content,
              date: new Date(data.date)
            };
          } catch (err) {
            console.error(`Error loading ${filename}:`, err);
            return null;
          }
        });

        const loadedPosts = (await Promise.all(postPromises)).filter(post => post !== null);
        
        if (loadedPosts.length === 0) {
          console.error('No posts loaded. Check that files exist in public/posts/');
        }
        
        // Sort by date (newest first)
        loadedPosts.sort((a, b) => b.date - a.date);
        
        setPosts(loadedPosts);
        setLoading(false);
      } catch (error) {
        console.error('Error loading posts:', error);
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-container">
        <div className="blog-loading">Loading posts...</div>
      </div>
    );
  }

  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);

  return (
    <div className="blog-container">
      <div className="blog-header">
        <h1 className="blog-title">Blog</h1>
        <p className="blog-subtitle">Thoughts, experiences, and insights from the NYC tech scene</p>
      </div>

      {/* Featured Post */}
      {featuredPost && (
        <div className="featured-post">
          <Link to={`/blog/${featuredPost.slug}`} className="featured-post-link">
            <div className="featured-post-content">
              <span className="featured-badge">Featured</span>
              <h2 className="featured-post-title">{featuredPost.title}</h2>
              <p className="featured-post-excerpt">{featuredPost.excerpt}</p>
              <div className="featured-post-meta">
                <span className="post-date">{formatDate(featuredPost.date)}</span>
                <span className="read-more">Read More →</span>
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Other Posts */}
      <div className="posts-grid">
        {otherPosts.map((post) => (
          <article key={post.slug} className="post-card">
            <Link to={`/blog/${post.slug}`} className="post-card-link">
              <div className="post-card-content">
                <h3 className="post-card-title">{post.title}</h3>
                <p className="post-card-excerpt">{post.excerpt}</p>
                <div className="post-card-meta">
                  <span className="post-date">{formatDate(post.date)}</span>
                </div>
              </div>
            </Link>
          </article>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="blog-empty">
          <p>No posts yet. Check back soon!</p>
        </div>
      )}
    </div>
  );
}

export default BlogList;

